import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconChevronLeft, IconChevronRight } from "@/components/icons";
import { SubmissionAnswersList } from "@/components/forms/SubmissionAnswersList";
import { submitForm } from "../../../../api/forms";
import { buildSubmitPayloadFromRefs } from "../../../../lib/forms/buildSubmitPayload";
import { formatSubmitError } from "../../../../lib/http/formatSubmitError";
import { ApiError } from "../../../../api/client";
import { scrollToField } from "../../../../lib/forms/scrollToField";
import { collectAllFields } from "../../../../lib/forms/visibility";
import { isFieldInline } from "../../../../lib/forms/fieldOptions";
import { buildExternalSubmissionView } from "../../../../lib/forms/externalSubmissionView";
import { readShowSubmissionAfterSubmit } from "../../../../lib/forms/formMeta";
import { submissionFromSubmitResponse } from "../../../../lib/forms/normalizeSubmission";
import {
  clearFormResponseDraft,
  loadFormResponseDraft,
  saveFormResponseDraft,
} from "../../../../lib/forms/responseDraft";
import { useFormVisibility } from "../../../../hooks/useFormVisibility";
import type { FormFieldHandle } from "../../../../components/forms/types";
import type { FieldDto } from "../../../../types/field";
import type { SectionDto } from "../../../../types/section";
import type { FormSubmissionDto } from "../../../../types/submission";
import SectionComponent, { type SectionComponentRefs } from "../section";

interface FormComponentProps {
  sections: SectionDto[];
  rootFields?: FieldDto[];
  description?: string;
  submitDisabled?: boolean;
  showSectionLabels?: boolean;
  sectionDisplayMode?: "all" | "step";
  showSubmissionAfterSubmit?: boolean;
  cityNames?: Record<string, string>;
}

const submitButtonClassName =
  "form-submit-btn inline-flex w-full items-center justify-center rounded-xl bg-brand-500 px-6 py-2.5 font-onest text-[15px] font-medium text-white shadow-md shadow-brand-500/25 transition-all duration-200 hover:bg-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:ml-auto sm:w-auto";

export default function FormComponent({
  sections,
  rootFields = [],
  description = "",
  submitDisabled = false,
  showSectionLabels = true,
  sectionDisplayMode = "all",
  showSubmissionAfterSubmit = false,
  cityNames,
}: FormComponentProps) {
  const navigate = useNavigate();
  const { formId } = useParams();
  const formDomId = `chm-form-${formId ?? "submit"}`;
  const publicFormId = formId?.trim() ?? "";
  const [isSending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<string | null>(null);
  const [completedSubmission, setCompletedSubmission] =
    useState<FormSubmissionDto | null>(null);
  const sectionsRefs = useRef<SectionComponentRefs[]>([]);
  const formRef = useRef<HTMLFormElement>(null);
  const pendingInvalidField = useRef<FormFieldHandle | null>(null);
  const stepMode = sectionDisplayMode === "step" && sections.length > 1;

  const allFields = useMemo(
    () => collectAllFields(sections, rootFields),
    [sections, rootFields],
  );
  const restoredDraft = useMemo(
    () =>
      loadFormResponseDraft(
        publicFormId,
        allFields.map((field) => field.name),
      ),
    [allFields, publicFormId],
  );
  const [currentStep, setCurrentStep] = useState(() =>
    stepMode ? (restoredDraft?.currentStep ?? 0) : 0,
  );
  const activeStep = Math.min(currentStep, Math.max(0, sections.length - 1));
  const isLastStep = activeStep === sections.length - 1;

  useEffect(() => {
    if (!pendingInvalidField.current) return;
    const frame = requestAnimationFrame(() => {
      scrollToField(pendingInvalidField.current);
      pendingInvalidField.current = null;
    });
    return () => cancelAnimationFrame(frame);
  }, [currentStep]);

  const { valuesByName, visibleByName, setFieldValue } = useFormVisibility(
    allFields,
    restoredDraft?.values,
  );

  useEffect(() => {
    if (completedSubmission) return;
    saveFormResponseDraft(publicFormId, {
      values: valuesByName,
      currentStep: stepMode ? activeStep : 0,
    });
  }, [activeStep, completedSubmission, publicFormId, stepMode, valuesByName]);

  const hasInlineFields = useMemo(
    () => allFields.some((field) => isFieldInline(field)),
    [allFields],
  );
  const introText = hasInlineFields ? description.trim() : "";
  const completedRows = useMemo(
    () =>
      completedSubmission
        ? buildExternalSubmissionView(
            completedSubmission,
            allFields,
            cityNames,
          )
        : [],
    [allFields, cityNames, completedSubmission],
  );

  const runClientValidation = (currentOnly = false) => {
    const indices =
      stepMode && currentOnly
        ? [activeStep]
        : sections.map((_, index) => index);
    let invalidCount = 0;
    let firstInvalidField: FormFieldHandle | null = null;
    let firstInvalidStep = -1;

    for (const index of indices) {
      const result = sectionsRefs.current[index]?.validate(visibleByName);
      if (!result || result.valid) continue;
      invalidCount += result.invalidCount;
      if (firstInvalidStep < 0) {
        firstInvalidStep = index;
        firstInvalidField = result.firstInvalidField;
      }
    }

    if (invalidCount > 0) {
      const parts: string[] = [];
      parts.push(
        invalidCount === 1
          ? "Заполните обязательное поле"
          : `Заполните обязательные поля (${invalidCount})`,
      );
      setFormErrors(parts.join(". "));

      if (stepMode && firstInvalidStep !== activeStep) {
        pendingInvalidField.current = firstInvalidField;
        setCurrentStep(firstInvalidStep);
      } else {
        requestAnimationFrame(() => scrollToField(firstInvalidField));
      }
    } else {
      setFormErrors(null);
    }

    return invalidCount === 0;
  };

  const goToStep = (index: number) => {
    setCurrentStep(index);
    setFormErrors(null);
    setSubmitError(null);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitDisabled || isSending) return;
    setSubmitError(null);

    if (stepMode && !isLastStep) {
      if (runClientValidation(true)) goToStep(activeStep + 1);
      return;
    }

    if (!runClientValidation()) return;

    const payload = buildSubmitPayloadFromRefs(
      allFields,
      sectionsRefs.current,
      visibleByName,
    );

    setSending(true);
    try {
      const response = await submitForm(publicFormId, { data: payload });
      clearFormResponseDraft(publicFormId);
      const showAnswers =
        readShowSubmissionAfterSubmit(response) ?? showSubmissionAfterSubmit;
      if (showAnswers) {
        setCompletedSubmission(
          submissionFromSubmitResponse(response, payload),
        );
        return;
      }
      navigate(`/success?form-id=${formId}`);
    } catch (err) {
      if (import.meta.env.DEV && err instanceof ApiError) {
        console.error(
          "[chmforms] submit failed",
          err.status,
          err.errors ?? err.message,
        );
      }
      setSubmitError(formatSubmitError(err));
    } finally {
      setSending(false);
    }
  };

  if (completedSubmission) {
    return (
      <div className="space-y-5">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 sm:px-5">
          <p className="font-onest text-sm font-medium text-emerald-900 sm:text-base">
            Спасибо, ваше сообщение отправлено.
          </p>
        </div>
        <SubmissionAnswersList rows={completedRows} />
        {formId ? (
          <div className="px-1 pb-2 sm:px-0">
            <a
              href={`/forms/${formId}`}
              className="inline-flex w-full items-center justify-center rounded-xl bg-brand-500 px-6 py-2.5 font-onest text-[15px] font-medium text-white shadow-md shadow-brand-500/25 transition-colors hover:bg-brand-600 sm:w-auto"
            >
              Заполнить форму ещё раз
            </a>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <form
        ref={formRef}
        id={formDomId}
        onSubmit={handleOnSubmit}
        className="space-y-2 overflow-visible pb-24 sm:pb-0"
      >
        {stepMode ? (
          <p
            className="font-onest text-sm font-medium text-ink-muted"
            aria-live="polite"
          >
            Шаг {activeStep + 1} из {sections.length}
          </p>
        ) : null}

        {sections.map((section, idx) => (
          <div
            key={`${section.id ?? section.label}-${idx}`}
            className={stepMode && idx !== activeStep ? "hidden" : undefined}
            aria-hidden={stepMode && idx !== activeStep ? true : undefined}
          >
            <SectionComponent
              ref={(instance) => {
                sectionsRefs.current[idx] = instance;
              }}
              section={section}
              showLabel={showSectionLabels}
              visibleByName={visibleByName}
              valuesByName={valuesByName}
              onFieldValueChange={setFieldValue}
              introText={idx === 0 ? introText : undefined}
            />
          </div>
        ))}

        <footer className="mt-5 space-y-3 border-t border-slate-200 pt-5">
          {formErrors ? (
            <div
              className="form-alert-error rounded-xl border border-red-200 bg-red-50 px-4 py-3 scroll-mt-24 sm:scroll-mt-8"
              role="alert"
              aria-live="polite"
            >
              <p className="text-sm font-medium text-red-800 font-onest form-field-error">
                {formErrors}
              </p>
            </div>
          ) : null}

          {submitError ? (
            <div
              className="form-alert-error rounded-xl border border-red-200 bg-red-50 px-4 py-3"
              role="alert"
            >
              <p className="text-sm text-red-700 font-onest form-field-error">
                {submitError}
              </p>
            </div>
          ) : null}

          <div className="hidden sm:flex sm:justify-end">
            {stepMode && activeStep > 0 ? (
              <button
                type="button"
                disabled={isSending}
                onClick={() => goToStep(activeStep - 1)}
                className="mr-3 inline-flex items-center gap-1 rounded-lg px-4 py-2.5 font-onest text-sm font-medium text-ink-muted hover:bg-slate-100 disabled:opacity-50"
              >
                <IconChevronLeft size={16} />
                Назад
              </button>
            ) : null}
            <button
              type="submit"
              disabled={isSending || submitDisabled}
              className={submitButtonClassName}
            >
              {submitDisabled
                ? "Приём ответов закрыт"
                : isSending
                  ? "Отправка…"
                  : stepMode && !isLastStep
                    ? "Далее"
                    : "Отправить"}
              {stepMode && !isLastStep && !submitDisabled ? (
                <IconChevronRight size={16} className="ml-1" />
              ) : null}
            </button>
          </div>
        </footer>
      </form>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-8px_24px_rgba(41,53,69,0.08)] backdrop-blur-sm pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:hidden">
        <div className="flex gap-2">
          {stepMode && activeStep > 0 ? (
            <button
              type="button"
              disabled={isSending}
              onClick={() => goToStep(activeStep - 1)}
              className="inline-flex shrink-0 items-center justify-center gap-1 rounded-lg border border-slate-200 px-3 font-onest text-sm font-medium text-ink-muted disabled:opacity-50"
            >
              <IconChevronLeft size={16} />
              Назад
            </button>
          ) : null}
          <button
            type="submit"
            form={formDomId}
            disabled={isSending || submitDisabled}
            className={submitButtonClassName}
          >
            {submitDisabled
              ? "Приём ответов закрыт"
              : isSending
                ? "Отправка…"
                : stepMode && !isLastStep
                  ? "Далее"
                  : "Отправить"}
            {stepMode && !isLastStep && !submitDisabled ? (
              <IconChevronRight size={16} className="ml-1" />
            ) : null}
          </button>
        </div>
      </div>
    </>
  );
}
